import { Component, OnInit, OnDestroy, inject, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { PatternDef } from 'src/app/core/dto';
import { OverlayModule } from '@angular/cdk/overlay';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTabsModule } from '@angular/material/tabs';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDialog } from '@angular/material/dialog';
import { TranslateModule } from '@ngx-translate/core';
import { RouterModule } from '@angular/router';
import { UserDisplayUnitsPipe } from '../../pipes/units.pipe';
import { Xl200Service } from '../../services/xl200.service';
import { FlatViewLauncherComponent } from 'src/app/main/punch-pattern/flat-view-launcher/flat-view-launcher.component';

@Component({
  selector: 'app-punch-pattern-preview',
  templateUrl: './punch-pattern-preview.component.html',
  styleUrls: ['./punch-pattern-preview.component.scss'],
  imports: [
    CommonModule,
    FormsModule,
    OverlayModule,
    MatProgressSpinnerModule,
    MatTabsModule,
    MatCheckboxModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    TranslateModule,
    RouterModule,
    UserDisplayUnitsPipe,
  ],
})
export class PunchPatternPreviewComponent implements OnInit, OnDestroy {
  @Input() patternId: string = '';
  /**
   * Order-line context for the ad-hoc visualization launcher. When set, the popup header shows
   * a Visualize button that opens the launcher prefilled from the line (scheduled machine,
   * tooling code, material, length); with no scheduled machine the launcher defaults the
   * machine from the tooling code's assignments.
   */
  @Input() visualizeContext?: {
    machineNumber?: number | null;
    toolingCode?: string;
    materialCode?: string;
    lengthIn?: number;
  };
  pattern;
  punches;
  availableMacros;
  mainWidth = 1000;
  bodyWidth = 0;
  bodyHeight = 0;
  tableHeight = 187;
  mainHeight = 300;
  loadingWidth = 500;
  isOpen = false;
  isMouseOnMain = false;
  isMouseOnContent = false;
  offsetX = 0;
  offsetY = 0;
  loadingPos = {
    top: '0px',
    left: '0px',
  };

  timeoutId;

  punchColumns = [
    {
      field: 'idType',
      displayName: 'punchType',
    },
    {
      field: 'toolId',
      displayName: 'tool',
    },
    {
      field: 'xOffset',
      displayName: 'xOffset',
    },
    {
      field: 'xReference',
      displayName: 'xReference',
    },
    {
      field: 'yOffset',
      displayName: 'yOffset',
    },
    {
      field: 'yReference',
      displayName: 'yReference',
    },
    {
      field: 'ySegment',
      displayName: 'segment',
    },
  ];

  private xl200Service = inject(Xl200Service);
  private dialog = inject(MatDialog);

  openVisualize() {
    // Close the hover popup so the dialog isn't fighting it for the pointer.
    this.isOpen = false;
    this.isMouseOnMain = false;
    this.isMouseOnContent = false;
    this.dialog.open(FlatViewLauncherComponent, {
      width: '440px',
      data: {
        patternId: this.patternId,
        patternName: this.pattern?.patternName,
        machineNumber: this.visualizeContext?.machineNumber,
        toolingCode: this.visualizeContext?.toolingCode,
        materialCode: this.visualizeContext?.materialCode,
        lengthIn: this.visualizeContext?.lengthIn,
      },
    });
  }

  onGetData() {
    const patternId = this.patternId;
    this.xl200Service.getPunchPatternById(patternId).subscribe((response) => {
      this.pattern = response;
      const rows = this.pattern.punches.length;
      const height = 113 + rows * 40;
      console.log('pattern', this.pattern.punches);

      if (height < 300) {
        this.mainHeight = 300;
      } else if (height > 600) {
        this.mainHeight = 600;
        this.tableHeight = 487;
      } else {
        this.mainHeight = height;
        this.tableHeight = rows * 40;
      }
    });
  }

  onShowTooltip(e) {
    let pos = e.target.getBoundingClientRect();
    if (!this.patternId) {
      return;
    }
    this.timeoutId = setTimeout(() => {
      this.isMouseOnMain = true;
      this.onGetPosition(pos);
      if (!this.pattern) {
        setTimeout(() => {
          this.onGetData();
        }, 300);
      }
    }, 200);
  }

  onHideTooltip() {
    clearTimeout(this.timeoutId);
    if (this.isMouseOnMain) {
      this.isMouseOnMain = false;
      setTimeout(() => {
        if (!this.isMouseOnContent) {
          this.isOpen = false;
        }
      });
    }
  }

  onShowTooltip1() {
    this.isMouseOnContent = true;
  }

  onHideTooltip1() {
    this.isMouseOnContent = false;
    setTimeout(() => {
      if (!this.isMouseOnContent && !this.isMouseOnMain) {
        this.isOpen = false;
      }
    });
  }

  onGetPosition(pos) {
    let realTop = 0;
    let realRight = 0;
    const { top, left, bottom, right } = pos;
    const width = right - left;

    // Re-measure at open time — the ngOnInit values go stale when the window is resized.
    this.bodyWidth = window.innerWidth;
    this.bodyHeight = window.innerHeight;

    // Right-Down
    if (right < this.bodyWidth / 3 && top < this.bodyHeight / 3) {
      realRight = 0;
      realTop = 0;
      this.loadingPos = { top: `0px`, left: '0px' };
    } // Center-Down
    else if (
      right < (this.bodyWidth * 2) / 3 &&
      right >= this.bodyWidth / 3 &&
      top < this.bodyHeight / 3
    ) {
      realRight = width / 2 - this.mainWidth / 2;
      realTop = 0;
      this.loadingPos = { top: `0px`, left: `${this.mainWidth / 2 - this.loadingWidth / 2}px` };
    } // Left-Down
    else if (right >= (this.bodyWidth * 2) / 3 && top < this.bodyHeight / 3) {
      realRight = 0 - this.mainWidth + width;
      realTop = 0;
      this.loadingPos = { top: `0px`, left: `${this.mainWidth - this.loadingWidth}px` };
    } // Right-Middle
    else if (
      right < this.bodyWidth / 2 &&
      top >= this.bodyHeight / 3 &&
      top < (this.bodyHeight * 2) / 3
    ) {
      realRight = right - left - 10;
      realTop = 0 - this.mainHeight / 2 - 10;
      this.loadingPos = { top: `${this.mainHeight / 2 - 20}px`, left: '0px' };
    } // Left-Middle
    else if (
      right >= this.bodyWidth / 2 &&
      top >= this.bodyHeight / 3 &&
      top < (this.bodyHeight * 2) / 3
    ) {
      realRight = 0 - this.mainWidth;
      realTop = 0 - this.mainHeight / 2 - 10;
      this.loadingPos = {
        top: `${this.mainHeight / 2 - 20}px`,
        left: `${this.mainWidth - this.loadingWidth}px`,
      };
    } // Right-Up
    else if (right < this.bodyWidth / 3 && top >= (this.bodyHeight * 2) / 3) {
      realRight = 0;
      realTop = 0 - this.mainHeight - 25;
      this.loadingPos = { top: `${this.mainHeight - 40}px`, left: '0px' };
    } // Center-Up
    else if (
      right < (this.bodyWidth * 2) / 3 &&
      right >= this.bodyWidth / 3 &&
      top >= (this.bodyHeight * 2) / 3
    ) {
      realRight = width / 2 - this.mainWidth / 2;
      realTop = 0 - this.mainHeight - 10;
      this.loadingPos = {
        top: `${this.mainHeight - 50}px`,
        left: `${this.mainWidth / 2 - this.loadingWidth / 2}px`,
      };
    } // Left-Up
    else {
      realRight = 0 - this.mainWidth + width;
      realTop = 0 - this.mainHeight - 10;
      this.loadingPos = {
        top: `${this.mainHeight - 40}px`,
        left: `${this.mainWidth - this.loadingWidth}px`,
      };
    }
    // Clamp to the viewport. The quadrant branches above pick a side but assume the fixed
    // 1000px-wide (up to 600px-tall) popup fits there, which cuts off the far edge on
    // narrower windows. The overlay anchors at the trigger's bottom-left, so its screen
    // position is (left + offsetX, bottom + offsetY).
    const margin = 8;
    const minX = margin - left;
    const maxX = Math.max(this.bodyWidth - this.mainWidth - margin - left, minX);
    realRight = Math.min(Math.max(realRight, minX), maxX);
    const minY = margin - bottom;
    const maxY = Math.max(this.bodyHeight - this.mainHeight - margin - bottom, minY);
    realTop = Math.min(Math.max(realTop, minY), maxY);

    this.offsetX = realRight;
    this.offsetY = realTop;
    this.isOpen = true;
  }

  trackByKey = (index: number): number => {
    return index;
  };

  ngOnInit(): void {
    this.bodyWidth = window.innerWidth;
    this.bodyHeight = window.innerHeight;
  }

  ngOnDestroy() {
    this.isOpen = false;
  }
}
